import React, { useState } from 'react';
import axios from 'axios';
import { ShieldAlert, Trash2, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeleteAccount = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Please enter your email and password.'); return; }
    setConfirm(true);
  };

  const runDelete = async () => {
    setBusy(true); setError('');
    try {
      const login = await axios.post(`${API}/seller/login`, { email: form.email, password: form.password });
      const token = login.data.token;
      await axios.delete(`${API}/seller/account`, { headers: { Authorization: `Bearer ${token}` } });
      setConfirm(false);
      setDone(true);
    } catch (err) {
      const code = err?.response?.status;
      setError(code === 401 ? 'Incorrect email or password.' : (err?.response?.data?.detail || 'Something went wrong. Please try again.'));
      setConfirm(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0b1510] text-white flex flex-col items-center px-6 py-12">
      <div className="w-full max-w-lg">
        <a href="/" data-testid="delete-back-home" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8"><ArrowLeft size={16} /> Back to FLIX</a>
        <span className="brand text-3xl">flix<span className="text-[#3ddc9a]">.</span></span>

        {done ? (
          <div className="mt-10 text-center">
            <div className="w-20 h-20 rounded-3xl bg-[#0e9f6e]/15 flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} className="text-[#3ddc9a]" /></div>
            <h1 className="font-display text-3xl font-700">Account deleted</h1>
            <p className="text-white/55 mt-3">Your FLIX account and all associated data have been permanently removed. This action cannot be undone.</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl font-700 mt-6">Delete your account</h1>
            <p className="text-white/60 mt-3 leading-relaxed">
              You can permanently delete your FLIX seller account at any time. Deleting your account will
              <b className="text-white"> permanently remove all of your data</b>, including:
            </p>
            <ul className="mt-4 space-y-2 text-white/70 text-sm">
              <li className="flex gap-2"><span className="text-[#3ddc9a]">•</span> Your profile, email, phone and login credentials</li>
              <li className="flex gap-2"><span className="text-[#3ddc9a]">•</span> Your saved customers</li>
              <li className="flex gap-2"><span className="text-[#3ddc9a]">•</span> Your transaction and sales history</li>
              <li className="flex gap-2"><span className="text-[#3ddc9a]">•</span> Your settings (tax rate, preferences)</li>
            </ul>
            <div className="mt-4 flex items-start gap-2 rounded-2xl bg-[#f0b429]/10 border border-[#f0b429]/25 p-3 text-xs text-[#f0b429]">
              <ShieldAlert size={15} className="mt-0.5 shrink-0" />
              <span>Your connected Stripe account is managed by Stripe and is not deleted here. Make sure any pending payouts have cleared before deleting. This action is permanent and cannot be undone.</span>
            </div>

            <form onSubmit={submit} className="mt-7 space-y-3">
              <p className="text-sm text-white/60">Confirm it's you by entering your account details:</p>
              <input data-testid="delete-email-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required placeholder="Email"
                className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
              <input data-testid="delete-password-input" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} required placeholder="Password"
                className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
              {error && <p data-testid="delete-error" className="text-sm text-[#ff6b5e]">{error}</p>}
              <button data-testid="delete-request-button" type="submit" disabled={busy}
                className="w-full bg-[#d64545] text-white rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                <Trash2 size={18} /> Request account deletion
              </button>
            </form>
            <p className="text-xs text-white/40 mt-4">Need help? Contact support and we'll process your request within 30 days.</p>
          </>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => !busy && setConfirm(false)} />
          <div className="relative w-full max-w-sm bg-[#12221a] border border-white/10 rounded-3xl p-6">
            <div className="w-12 h-12 rounded-2xl bg-[#d64545]/15 flex items-center justify-center mb-4"><ShieldAlert size={22} className="text-[#ff6b5e]" /></div>
            <h3 className="font-display text-xl font-700">Permanently delete account?</h3>
            <p className="text-sm text-white/55 mt-2">This will erase your account and all data for <b className="text-white">{form.email}</b>. This cannot be undone.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirm(false)} disabled={busy} className="flex-1 h-12 rounded-full bg-white/8 text-white font-semibold">Cancel</button>
              <button data-testid="delete-confirm-button" onClick={runDelete} disabled={busy} className="flex-1 h-12 rounded-full bg-[#d64545] text-white font-semibold flex items-center justify-center disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
